import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const CookiesPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  
  const getText = (en, pt) => language === 'pt' ? pt : en;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1a5c3a] text-white py-12">
        <div className="max-w-4xl mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-white/70 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft size={20} />
            {getText('Back', 'Voltar')}
          </button>
          <h1 className="text-3xl font-bold">
            {getText('Cookie Policy', 'Política de Cookies')}
          </h1>
          <p className="text-white/70 mt-2">
            {getText('Last updated: January 2025', 'Última atualização: Janeiro 2025')}
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 space-y-8">
          
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('What Are Cookies?', 'O Que São Cookies?')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {getText(
                'Cookies are small text files that are stored on your computer or mobile device when you visit our website. They help us provide you with a better experience by remembering your preferences and understanding how you use our site.',
                'Cookies são pequenos ficheiros de texto que são armazenados no seu computador ou dispositivo móvel quando visita o nosso website. Ajudam-nos a proporcionar uma melhor experiência ao lembrar as suas preferências e compreender como utiliza o nosso site.'
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('Types of Cookies We Use', 'Tipos de Cookies que Utilizamos')}
            </h2>
            
            <div className="space-y-4">
              <div className="p-4 bg-gray-50 rounded-xl">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {getText('Essential Cookies', 'Cookies Essenciais')}
                </h3>
                <p className="text-gray-600 text-sm">
                  {getText(
                    'These cookies are necessary for the website to function properly. They enable basic features like page navigation and access to secure areas.',
                    'Estes cookies são necessários para o funcionamento adequado do website. Permitem funcionalidades básicas como navegação de página e acesso a áreas seguras.'
                  )}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {getText('Functional Cookies', 'Cookies Funcionais')}
                </h3>
                <p className="text-gray-600 text-sm">
                  {getText(
                    'These cookies remember your preferences and choices (like language preference) to provide a more personalized experience.',
                    'Estes cookies lembram as suas preferências e escolhas (como preferência de idioma) para proporcionar uma experiência mais personalizada.'
                  )}
                </p>
              </div>

              <div className="p-4 bg-gray-50 rounded-xl">
                <h3 className="font-semibold text-gray-900 mb-2">
                  {getText('Analytics Cookies', 'Cookies de Análise')}
                </h3>
                <p className="text-gray-600 text-sm">
                  {getText(
                    'These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously.',
                    'Estes cookies ajudam-nos a compreender como os visitantes interagem com o nosso website, recolhendo e reportando informação de forma anónima.'
                  )}
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('How to Manage Cookies', 'Como Gerir Cookies')}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {getText(
                'You can control and manage cookies in various ways:',
                'Pode controlar e gerir cookies de várias formas:'
              )}
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>{getText('Browser settings: Most browsers allow you to refuse or delete cookies', 'Definições do navegador: A maioria dos navegadores permite recusar ou eliminar cookies')}</li>
              <li>{getText('Third-party tools: Various tools exist to manage tracking technologies', 'Ferramentas de terceiros: Existem várias ferramentas para gerir tecnologias de rastreamento')}</li>
              <li>{getText('Our website: Some cookies can be managed through our cookie consent banner', 'Nosso website: Alguns cookies podem ser geridos através do nosso banner de consentimento')}</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('Third-Party Cookies', 'Cookies de Terceiros')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {getText(
                'We may use third-party services that set their own cookies, such as Google Analytics for website analytics and WhatsApp for customer communication. These services have their own privacy policies.',
                'Podemos utilizar serviços de terceiros que definem os seus próprios cookies, como o Google Analytics para análise do website e o WhatsApp para comunicação com clientes. Estes serviços têm as suas próprias políticas de privacidade.'
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('Updates to This Policy', 'Atualizações a Esta Política')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {getText(
                'We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated revision date.',
                'Podemos atualizar esta Política de Cookies de tempos a tempos. Quaisquer alterações serão publicadas nesta página com uma data de revisão atualizada.'
              )}
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {getText('Contact Us', 'Contacte-nos')}
            </h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              {getText(
                'If you have any questions about our use of cookies, please contact us:',
                'Se tiver alguma questão sobre o nosso uso de cookies, por favor contacte-nos:'
              )}
            </p>
            <div className="p-4 bg-gray-50 rounded-xl">
              <p className="text-gray-700 font-medium">Sancur Property Services</p>
              <p className="text-gray-600">Email: info@sancurservices.com</p>
              <p className="text-gray-600">Phone: +353 89 950 2499</p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default CookiesPage;
